import styled from '@emotion/styled';
import {
    useUndoValue,
    VenueLocation,
    VenueValues,
} from '@eventbrite/event-create-ui';
import { gettext } from '@eventbrite/i18n';
import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAppContext } from '../../../../AppContext';
import { AutoCreateEventFormValues } from '../../types';

export enum VenueSearchTestId {
    Container = 'autocompleteLocationContainer',
    Field = 'autocompleteLocationField',
    NotFound = 'autocompleteLocationNotFound',
    Content = 'autocompleteLocationContent',
}

export interface VenueSearchProps {
    onChange: (venue: VenueValues) => void;
}

export const VenueSearch = ({ onChange }: VenueSearchProps) => {
    const { _activeOrganizationId: organizationId } = useAppContext();
    const { control, setError, clearErrors, setValue } =
        useFormContext<AutoCreateEventFormValues>();

    const [memoVenue, setMemoVenue] = useUndoValue<
        AutoCreateEventFormValues,
        'venue'
    >('venue');

    const openVenueOptions = useCallback(() => {
        setValue('showVenueOptions', true);
    }, [setValue]);

    const onError = () => {
        setError('venue.name', {
            type: 'custom',
            message: gettext(
                'We could not find the location you entered. Please try again.',
            ).toString(),
        });
    };

    const onSelectVenue = (venue: VenueValues) => {
        clearErrors('venue');
        onChange(venue);
        setMemoVenue(venue);
    };

    const onResetVenue = () => {
        if (memoVenue) {
            setValue('venue.name', memoVenue.name);
        }
    };

    return (
        <Container data-testid={VenueSearchTestId.Container} role="combobox">
            <Controller
                name="venue.name"
                control={control}
                rules={{
                    required: gettext('Location is required').toString(),
                }}
                render={({ field, fieldState: { error } }) => (
                    <VenueLocation
                        {...field}
                        label={gettext('Location').toString()}
                        onSelectVenue={onSelectVenue}
                        onResetVenue={onResetVenue}
                        onError={onError}
                        error={error}
                        organizationId={organizationId}
                        onNoResults={openVenueOptions}
                    />
                )}
            />
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;
